import { ESystemRoles } from "@constants/SystemRoles";
import { IAppreciationDistribute } from "./Appreciation/AppreciationDistribute.type";
import { IArea } from "./Area.type";
import { IBase } from "./Base";
import { IUserCareer } from "./Career.type";
import { IEndorsement } from "./Endorsement.type";
import { IGoal } from "./Goal.type";
import { IRole } from "./Role.type";
import { ISkill } from "./Skills.type";
// eslint-disable-next-line import/no-cycle
import { TIcons } from "./Icons.types";
import { IAICV } from "./Jobyfine.type";
import { ITeam } from "./Team.type";
import { IVendor } from "./Vendor.types";

export enum EUserSeniority {
  UNDEFINED = "UNDEFINED",
  JUNIOR = "JUNIOR",
  SEMISENIOR = "SEMISENIOR",
  SENIOR = "SENIOR",
  EXPERT = "EXPERT",
  TRAINEE = "TRAINEE"
}

export interface EnglishLevelUpdate {
  newEnglishLevel?: EnglishLevelEnum;
  oldEnglishLevel?: EnglishLevelEnum;
  updatedAt?: Date;
  userId?: string;
}

export enum GenderEnum {
  MALE = "MALE",
  FEMALE = "FEMALE"
}

export interface UserSeniorityLog {
  seniority?: EUserSeniority;
  date?: Date;
  validator?: string;
}

export interface IndustriesUser {
  industry: IIndustry;
  score: number;
}

export interface IIndustry {
  id: string;
  name: string;
  description: string;
}

export interface IUser {
  id: string;
  isi?: boolean;
  updatedAt: Date;
  createdAt: Date;
  deletedAt: Date;
  name: string;
  lastname: string;
  email: string;
  phone: string;
  shortBio: string;
  username: string;
  address: IAddress[];
  contact: IContact[];
  card: ICard[];
  recoveryPasswordToken: String;
  password: string;
  networkType: string;
  systemRoles: IUserRoleVendor[];
  vendorList: IUserVendorListType[];
  selectedVendorId: string;
  selectedVendor: IVendor;
  random4digits: number;
  active: boolean;
  profileImage: string;
  root: boolean;
  role: IRole;
  area: IArea;
  systemRole: ESystemRoles;
  leader: boolean;
  teams?: ITeam[];
  userRealTeams?: ITeam[];
  activeCareer?: IUserCareer;
  lastAppreciation: IAppreciationDistribute;
  coverImage: string;
  skills?: ISkill[];
  endorsements?: IEndorsement[];
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  instagram?: string;
  goal: IGoal;
  dailyQuestionTasks?: string[];
  lang?: string;
  github?: string;
  userRank?: number;
  userLevel?: IUserLevel;
  lastActivityDate?: Date;
  coins?: number;
  certifications?: IUserCertification[];
  englishLevel?: EnglishLevelEnum;
  englishLevelUpdates?: EnglishLevelUpdate[];
  country?: string;
  seniority?: EUserSeniority;
  seniorityLog: UserSeniorityLog[];
  userLeaders: IUser[];
  birthday?: Date;
  birthDate?: Date;
  assignable?: boolean;
  assigned?: boolean;
  availability?: boolean;
  countryCode?: string;
  nationality?: string;
  activeSince?: Date;
  userFeedbacks?: number;
  coinsAdmin?: boolean;
  bukExternalJobName?: string;
  jobyfineAdmin?: boolean;
  jobyfineRequester?: boolean;
  jobyfineRecruiter?: boolean;
  adManager?: IUser;
  adCleverId?: string;
  adExternalId?: string;
  adExternalJobTitle?: string;
  billability?: number;
  lastUsedIp?: string;
  hrManager?: boolean;
  cloudManager?: boolean;
  operationsManager?: boolean;
  financeManager?: boolean;
  anonymousBoxAdmin?: boolean;
  aiCVInfo?: IAICV;
  isBirthday?: boolean;
  isAnniversary?: boolean;
  isNewUser?: boolean;
  birthdayMessageId?: string;
  anniversaryMessageId?: string;
  newUsersMessageId?: string;
  cvUrl?: string;
  expelledFromGuilds?: string[];
  feedbackAdmin?: boolean;
  magazineAdmin?: boolean;
  trainer?: boolean;
  availableRecognitions?: string;
  coinsHistoricallyReceived?: number;
  tags?: string[];
  industries?: IndustriesUser[];
  isOffboarding?: boolean;
  offboardingDate?: Date;
  availableOnJobyfineMarketplace?: boolean;
  availableFromDate?: string;
}

export interface ISelectedUsers {
  user: IUser;
  appreciationDistributes: IAppreciationDistribute[];
}

export interface IAddress {
  id?: string;
  name?: string;
  info: string;
  street: string;
  number: string;
}

export interface IContact {
  id: string;
  type: string;
  number: string;
}

export interface ICard {
  id: string;
  creditCardType: string;
  last4CardDigits: string;
  tbkUser: string;
  authCode: string;
}

export interface IUserRoleVendor {
  role: string;
  vendorId: string;
}

export enum EUserSystemRole {
  USER = "USER",
  ADMIN = "ADMIN",
  LEADER = "LEADER",
  APPLICANT = "APPLICANT"
}

export interface IUserVendorListType {
  id: string;
  name: string;
}

export interface IUserOnboardingSteps {
  code: string;
  completed: boolean;
  description: string;
  mantainerUrl: string;
  icon: TIcons;
  title: string;
}

export interface IUserOnboarding {
  id: string;
  completedPercentage: number;
  steps: IUserOnboardingSteps[];
}

export interface IUserLevel extends IBase {
  user: string;
  experience: number;
  level: number;
  vendor: string;
  uniqueActions?: string[];
  lives?: number;
  nextLevelExp?: number;
}

export interface IUserCertification extends IBase {
  name: string;
  expiracyDate?: Date;
  url?: string;
  certificationUrl?: string;
  user: string;
  deleted: boolean;
  type?: ECertificationType;
  certificationType?: ETypeCertification;
  emitDate?: Date;
}

export enum ETypeCertification {
  CERTIFICATION = "CERTIFICATION",
  COURSE = "COURSE",
  ACREDITATION = "ACREDITATION",
  CREDENTIAL = "CREDENTIAL"
}

export enum ECertificationType {
  GOOGLE = "GOOGLE",
  MICROSOFT = "MICROSOFT",
  MONGODB = "MONGODB",
  AZURE = "AZURE",
  AWS = "AWS",
  GITHUB = "GITHUB",
  GCP = "GCP",
  ORACLE = "ORACLE",
  ISQI = "ISQI",
  BRIGHTEST = "BRIGHTEST",
  CERTIFPROF = "CERTIFPROF",
  SCRUMALLIANCE = "SCRUMALLIANCE",
  SCRUMINC = "SCRUMINC",
  SCRUMORG = "SCRUMORG",
  ICAGILE = "ICAGILE",
  PROKANBAN = "PROKANBAN",
  CODERHOUSE = "CODERHOUSE",
  OTHER = "OTHER"
}

export enum EnglishLevelEnum {
  A0 = "A0 - Grogu",
  A1 = "A1 - R2D2",
  A2 = "A2 - Leia",
  B1 = "B1 - Obi-Wan",
  B2 = "B2 - Darth Vader",
  C1 = "C1 - Luke",
  C2 = "C2 - Yoda"
}

export interface IUserDailyLogQuestion {
  question: string;
  isCorrect?: boolean;
  options?: string[];
}

export interface IUserDailyLog {
  date: Date;
  questions?: IUserDailyLogQuestion[];
}

export interface IUserDailyQuestionLog {
  user: IUser;
  date?: Date;
  answered: boolean;
  points: number;
  role?: string;
  vendor?: string;
  sendedQuestions?: string[];
  questions: string[];
  validating?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IOnboardingListItem
  extends Partial<Omit<IUserOnboardingSteps, "mantainerUrl">> {
  title: string;
  icon: TIcons;
  mantainerUrl: (dynamicUrl?: string) => string;
}

export enum EOnboardingSteps {
  CREATE_TEAM = "CREATE_TEAM",
  CREATE_APPRECIATION = "CREATE_APPRECIATION",
  DISTRIBUTE_APPRECIATION = "DISTRIBUTE_APPRECIATION",
  CREATE_MULTIMEDIA = "CREATE_MULTIMEDIA",
  CREATE_ITEM_PATH = "CREATE_ITEM_PATH",
  CREATE_CAREER = "CREATE_CAREER",
  ASSIGN_CAREER = "ASSIGN_CAREER",
  CREATE_GOAL = "CREATE_GOAL",
  CREATE_TASK = "CREATE_TASK",
  CREATE_ROLES = "CREATE_ROLES",
  CREATE_MATURITY_LEVEL = "CREATE_MATURITY_LEVEL",
  CREATE_DIMENSION = "CREATE_DIMENSION",
  INVITE_USERS = "INVITE_USERS",
  ADD_USER_TO_TEAM = "ADD_USER_TO_TEAM",
  CREATE_FORM = "CREATE_FORM"
}

export type TOnboardingListItems = {
  [_step in EOnboardingSteps]: IOnboardingListItem;
};
